import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAgenti, createAgente, updateAgente, deleteAgente, sendAgenteWelcomeEmail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Users, Building2, Percent, Euro, Loader2, Trash2, FileText, ExternalLink, Pencil, Mail, Send } from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const AgentiPage = () => {
  const [agenti, setAgenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agenteToDelete, setAgenteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogAgente, setEmailDialogAgente] = useState(null);
  const [emailPassword, setEmailPassword] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    password: '',
    percentuale_commissione: 20,
  });

  useEffect(() => {
    loadAgenti();
  }, []);

  const loadAgenti = async () => {
    try {
      const response = await getAgenti();
      setAgenti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento agenti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editMode && editingId) {
        // In modalità modifica, invia solo i campi con valori
        const updateData = {};
        if (formData.nome) updateData.nome = formData.nome;
        if (formData.cognome) updateData.cognome = formData.cognome;
        if (formData.email) updateData.email = formData.email;
        if (formData.password) updateData.password = formData.password;
        if (formData.percentuale_commissione !== undefined) updateData.percentuale_commissione = formData.percentuale_commissione;
        
        await updateAgente(editingId, updateData);
        toast.success('Agente aggiornato');
      } else {
        await createAgente(formData);
        toast.success('Agente creato');
      }
      setDialogOpen(false);
      resetForm();
      loadAgenti();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ nome: '', cognome: '', email: '', password: '', percentuale_commissione: 20 });
    setEditMode(false);
    setEditingId(null);
  };

  const handleEdit = (agente) => {
    setFormData({
      nome: agente.nome || '',
      cognome: agente.cognome || '',
      email: agente.email || '',
      password: '',
      percentuale_commissione: agente.percentuale_commissione || 20,
    });
    setEditingId(agente.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSendEmail = async (agente) => {
    // Apre il dialog per inserire la password
    setEmailDialogAgente(agente);
    setEmailPassword('');
    setEmailDialogOpen(true);
  };

  const handleConfirmSendEmail = async () => {
    if (!emailDialogAgente) return;
    
    setSendingEmail(emailDialogAgente.id);
    try {
      await sendAgenteWelcomeEmail(emailDialogAgente.id, emailPassword || null);
      toast.success(`Email inviata a ${emailDialogAgente.email}`);
      setEmailDialogOpen(false);
      setEmailDialogAgente(null);
      setEmailPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setSendingEmail(null);
    }
  };

  const openDeleteDialog = (agente) => {
    setAgenteToDelete(agente);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!agenteToDelete) return;
    setDeleting(true);
    try {
      await deleteAgente(agenteToDelete.id);
      toast.success('Agente eliminato');
      loadAgenti();
      setDeleteDialogOpen(false);
      setAgenteToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAgenti = agenti.filter(a => 
    a.nome?.toLowerCase().includes(search.toLowerCase()) ||
    a.cognome?.toLowerCase().includes(search.toLowerCase()) ||
    a.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="agenti-page">
      {/* Sezione Documentazione */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Documentazione Agenti
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Consulta guide, procedure e materiali formativi per la rete commerciale
                </p>
              </div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              onClick={() => window.open('https://drive.google.com/drive/folders/1XYlCZG09fNXFMJOAdtxF3nix3jnVd3wL?usp=sharing', '_blank')}
              data-testid="documentation-link"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Apri Documentazione
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Agenti
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Gestione rete commerciale</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="new-agente-btn" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Agente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editMode ? 'Modifica Agente' : 'Nuovo Agente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome">Nome {!editMode && '*'}</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    required={!editMode}
                    data-testid="input-nome-agente"
                  />
                </div>
                <div>
                  <Label htmlFor="cognome">Cognome {!editMode && '*'}</Label>
                  <Input
                    id="cognome"
                    value={formData.cognome}
                    onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                    required={!editMode}
                    data-testid="input-cognome-agente"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email">Email {!editMode && '*'}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required={!editMode}
                    data-testid="input-email-agente"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="password">{editMode ? 'Nuova Password (lascia vuoto per non cambiare)' : 'Password *'}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editMode}
                    placeholder={editMode ? 'Lascia vuoto per mantenere la password attuale' : ''}
                    data-testid="input-password-agente"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="percentuale">% Commissione</Label>
                  <Input
                    id="percentuale"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.percentuale_commissione}
                    onChange={(e) => setFormData({...formData, percentuale_commissione: parseFloat(e.target.value) || 0})}
                    data-testid="input-percentuale-agente"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">
                  Annulla
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" disabled={submitting} data-testid="submit-agente-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editMode ? 'Salva Modifiche' : 'Crea Agente'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per nome o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-agenti"
          />
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredAgenti.map((agente) => (
          <Card key={agente.id} className={!agente.attivo ? 'opacity-60' : ''} data-testid={`agente-card-${agente.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <Users className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{agente.nome} {agente.cognome}</p>
                    <p className="text-xs text-slate-500">{agente.email}</p>
                  </div>
                </div>
                {agente.attivo ? (
                  <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                ) : (
                  <Badge variant="secondary">Disattivo</Badge>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="text-center p-2 bg-slate-50 rounded">
                  <div className="flex items-center justify-center gap-1 text-slate-600">
                    <Percent className="h-3 w-3" />
                    <span className="font-semibold">{agente.percentuale_commissione}%</span>
                  </div>
                  <p className="text-xs text-slate-400">Commissione</p>
                </div>
                <div className="text-center p-2 bg-slate-50 rounded">
                  <div className="flex items-center justify-center gap-1 text-slate-600">
                    <Building2 className="h-3 w-3" />
                    <span className="font-semibold">{agente.aziende_totali}</span>
                  </div>
                  <p className="text-xs text-slate-400">Aziende</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <div className="flex items-center justify-center gap-1 text-green-600">
                    <Euro className="h-3 w-3" />
                    <span className="font-semibold">{agente.commissione_totale?.toLocaleString('it-IT', { minimumFractionDigits: 0 })}</span>
                  </div>
                  <p className="text-xs text-slate-400">Maturate</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(agente)}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Modifica
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => handleSendEmail(agente)}
                  disabled={sendingEmail === agente.id}
                >
                  {sendingEmail === agente.id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
                  Invia Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => openDeleteDialog(agente)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Elimina
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredAgenti.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Nessun agente trovato
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="agenti-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Agente</th>
                  <th className="text-left p-4 font-medium text-slate-600">Email</th>
                  <th className="text-center p-4 font-medium text-slate-600">Commissione</th>
                  <th className="text-center p-4 font-medium text-slate-600">Aziende</th>
                  <th className="text-center p-4 font-medium text-slate-600">Elogi Demo</th>
                  <th className="text-center p-4 font-medium text-slate-600">Commissioni Maturate</th>
                  <th className="text-center p-4 font-medium text-slate-600">Stato</th>
                  <th className="text-center p-4 font-medium text-slate-600">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgenti.map((agente) => (
                  <tr 
                    key={agente.id} 
                    className={`border-b hover:bg-slate-50 transition-colors ${!agente.attivo ? 'opacity-50' : ''}`}
                    data-testid={`agente-row-${agente.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-100">
                          <Users className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{agente.nome} {agente.cognome}</p>
                          <p className="text-xs text-slate-500">{agente.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{agente.email}</td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Percent className="h-4 w-4 text-slate-400" />
                        <span className="font-semibold">{agente.percentuale_commissione}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span className="font-semibold">{agente.aziende_totali}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <Link to={`/agenti/${agente.id}/elogi`}>
                        <Button variant="outline" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                          <FileText className="h-4 w-4 mr-1" />
                          {agente.elogi_demo_count || 0}
                        </Button>
                      </Link>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-green-600">
                        <Euro className="h-4 w-4" />
                        <span className="font-semibold">
                          {agente.commissione_totale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      {agente.attivo ? (
                        <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                      ) : (
                        <Badge variant="secondary">Disattivo</Badge>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(agente)}
                          data-testid={`edit-agente-${agente.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleSendEmail(agente)}
                          disabled={sendingEmail === agente.id}
                          data-testid={`email-agente-${agente.id}`}
                        >
                          {sendingEmail === agente.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(agente)}
                          data-testid={`delete-agente-${agente.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredAgenti.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              Nessun agente trovato
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Invio Email con Password */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Email di Benvenuto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Stai per inviare l&apos;email di benvenuto a <strong>{emailDialogAgente?.email}</strong>.
            </p>
            <div className="space-y-2">
              <Label htmlFor="email_password">Password da includere (opzionale)</Label>
              <Input
                id="email_password"
                type="text"
                placeholder="Lascia vuoto per non includere la password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
              />
              <p className="text-xs text-slate-400">
                Se inserisci una password, verrà inclusa nell&apos;email. Altrimenti verrà inviata solo l&apos;email di benvenuto senza credenziali.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setEmailDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                onClick={handleConfirmSendEmail}
                disabled={sendingEmail === emailDialogAgente?.id}
              >
                {sendingEmail === emailDialogAgente?.id ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Invia Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Elimina Agente"
        description="Eliminando questo agente, le aziende associate non avranno più un agente di riferimento. Questa azione è irreversibile."
        itemName={agenteToDelete ? `${agenteToDelete.nome} ${agenteToDelete.cognome}` : ''}
        loading={deleting}
      />
    </div>
  );
};

export default AgentiPage;
